'use client';
import React, {HTMLAttributes, useEffect, useRef} from 'react';
import parser from 'html-react-parser';

import Image from 'next/image';
import RedirectButton from '../RedirectButton';
import sicilyImg from '../../../public/sicily.svg';
import {IModalsDictionary} from '@/dictionaries/components/modals/types';
import PlayYourTripButton from '../PlanYourTripButton';
import {parseOptions} from '@/utils/parseOptions';
import {mapCoordinatesToPosition} from '@/utils/mapCoordinatesToPosition';
import {cn} from '@/lib/utils';
import { getLinkToCTAVillasNear } from '@/utils/getLinkToCTAVillasNear';

interface ISurroundingSectionProps extends HTMLAttributes<HTMLDivElement> {
  modalsDict: IModalsDictionary;
  lang: Locale;
  regionSlug: string;
  userSavedSurroundings: GuideSurroundingPage[];
  surroundingInfo: {
    id: number;
    title: string;
    slug: string;
    description: string;
    image: string;
    villasNear: string;
    planYourTrip: string;
    removeFromTrip: string;
    zone: string;
    latitude: number;
    longitude: number;
  };
}

export const SurroundingSection: React.FC<ISurroundingSectionProps> = ({
  modalsDict,
  lang,
  userSavedSurroundings,
  regionSlug,
  surroundingInfo: {
    id,
    title,
    slug,
    description,
    villasNear,
    planYourTrip,
    removeFromTrip,
    image,
    latitude,
    longitude,
    zone,
  },
}) => {
  const [top, setTop] = React.useState(0);
  const [left, setLeft] = React.useState(0);

  const sicilyMap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const {width, height} = sicilyMap.current?.getBoundingClientRect() || {
        width: 0,
        height: 0,
      };
      const {left, top} = mapCoordinatesToPosition(
        {latitude, longitude},
        {width, height},
      );
      setTop(top);
      setLeft(left);
    };

    setTimeout(() => {
      handleResize();
    }, 0);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sicilyMap.current]);

  return (
    <section className="px-4 tablet:px-10 desktop:px-20 !mt-0 mb-28 space-y-8">
      <div className="relative w-full bg-Surrounding justify-center text-white items-center bg-center bg-cover overflow-hidden mb-[60px]">
        <div className="relative w-full aspect-[2/1]">
          <Image
            src={image}
            alt={title}
            fill
            sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 60vw, 100vw"
            priority
            className="absolute -z-10 object-cover"
          />
        </div>
      </div>

      <div className="max-w-[1520px] mx-auto">
        <div className=" flex flex-col w-full desktop:flex-row items-start">
          <div className="w-full desktop:w-1/2">
            <p className="text-gray-700 uppercase mb-6">{zone}</p>
            <h2 className="mb-6 text-title font-title font-bold">{title}</h2>
            <div className="mb-4 text-base">
              {parser(description, parseOptions)}
            </div>
            <div className="flex flex-row mt-14 flex-wrap gap-x-2 gap-y-2">
              <PlayYourTripButton
                surroundingInfos={{id, title}}
                dict={modalsDict}
                lang={lang}
                label={planYourTrip}
                removeLabel={removeFromTrip}
                userSavedSurroundings={userSavedSurroundings}
              />
              <RedirectButton href={getLinkToCTAVillasNear(lang, regionSlug, slug)} variant="default" className="w-72">
                {villasNear} {title}
              </RedirectButton>
            </div>
          </div>

          <div ref={sicilyMap} className="-z-10 relative">
            <Image src={sicilyImg} alt={'sicily map'} />
            <div
              className={cn(
                `w-6 h-6 -translate-x-3 -translate-y-3 rounded-full shadow-[0px_4px_8px_#00000033] border-white border-[5px] bg-secondary absolute`,
                {
                  hidden: !sicilyMap.current,
                },
              )}
              style={{top, left}}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
